import { Popover as BasePopover } from '@base-ui/react/popover'
import React from 'react'
import styled from 'styled-components'
import { semantic } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'
import { shadows } from 'sutra/tokens/shadows'

export interface PopoverProps {
  // The element the popover anchors to. It becomes the trigger itself (no
  // wrapper element), so it must accept a ref and spread unknown props onto
  // its DOM node.
  children: React.ReactElement
  content: React.ReactNode
  title?: React.ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  // Off by default (antd's was on).
  arrow?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  // Also opens on hover, after the antd default 100ms in and out. Click still
  // toggles it, unlike antd's `trigger="hover"`.
  openOnHover?: boolean
  // Set to false when `children` renders a non-<button> (a span, a div).
  nativeButton?: boolean
  // Whether focus moves into the popup on open (to its first tabbable element
  // by default). A popover opened by hover never moves focus either way.
  initialFocus?: boolean | React.RefObject<HTMLElement | null>
  // The popup is a dialog; without a `title` it needs a name.
  'aria-label'?: string
  zIndex?: number
  // Both land on the popup, so `styled(Popover)` and antd's `overlayStyle`
  // override its box.
  className?: string
  style?: React.CSSProperties
}

// Above antd modals (1000), popovers (1030) and dropdowns (1050), so a Sutra
// popover opened from inside any of them is visible, and below the Sutra
// Tooltip (1070) so a tooltip on the popover's own content still clears it.
export const DEFAULT_Z_INDEX = 1060

// antd's default mouseEnterDelay and mouseLeaveDelay (0.1s).
const HOVER_DELAY = 100

const ARROW_SIZE = 10

const Popup = styled(BasePopover.Popup)`
  box-sizing: border-box;
  padding: 12px 16px;
  color: ${semantic.fg};
  background: ${semantic.bg};
  border-radius: ${radii.sm}px;
  box-shadow: ${shadows.lg};
  outline: none;
  opacity: 1;
  transition: opacity ${motion.duration.fast}ms ${motion.ease};

  &[data-starting-style],
  &[data-ending-style] {
    opacity: 0;
  }
`

const Title = styled(BasePopover.Title)`
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
`

// A rotated square whose outer half is the visible tip; the inner half hides
// against the popup's own background.
const Arrow = styled(BasePopover.Arrow)`
  width: ${ARROW_SIZE}px;
  height: ${ARROW_SIZE}px;
  background: ${semantic.bg};
  rotate: 45deg;

  &[data-side='top'] {
    bottom: -${ARROW_SIZE / 2}px;
  }
  &[data-side='bottom'] {
    top: -${ARROW_SIZE / 2}px;
  }
  &[data-side='left'] {
    right: -${ARROW_SIZE / 2}px;
  }
  &[data-side='right'] {
    left: -${ARROW_SIZE / 2}px;
  }
`

export const Popover = ({
  children,
  content,
  title,
  side = 'top',
  align = 'center',
  arrow = false,
  open,
  onOpenChange,
  openOnHover = false,
  nativeButton = true,
  initialFocus,
  'aria-label': ariaLabel,
  zIndex = DEFAULT_Z_INDEX,
  className,
  style,
}: PopoverProps): React.ReactElement => (
  <BasePopover.Root
    open={open}
    onOpenChange={onOpenChange && ((next) => onOpenChange(next))}
  >
    <BasePopover.Trigger
      render={children}
      nativeButton={nativeButton}
      openOnHover={openOnHover}
      delay={HOVER_DELAY}
      closeDelay={HOVER_DELAY}
    />
    <BasePopover.Portal>
      <BasePopover.Positioner
        side={side}
        align={align}
        sideOffset={arrow ? ARROW_SIZE : 6}
        style={{ zIndex }}
      >
        <Popup
          data-sutra-popover=""
          aria-label={ariaLabel}
          initialFocus={initialFocus}
          className={className}
          style={style}
        >
          {arrow && <Arrow />}
          {title && <Title>{title}</Title>}
          {content}
        </Popup>
      </BasePopover.Positioner>
    </BasePopover.Portal>
  </BasePopover.Root>
)

Popover.displayName = 'Popover'
